import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  LinearProgress,
  Chip,
  Fade,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const POSITION_STYLES = {
  'bottom-right': { bottom: 24, right: 24 },
  'bottom-left': { bottom: 24, left: 24 },
  'top-right': { top: 24, right: 24 },
  'top-left': { top: 24, left: 24 },
};

const OnboardingChecklist = ({
  checklist,
  completedItems = [],
  isVisible = true,
  onItemClick,
  onItemComplete,
  onDismiss,
  onStartTour,
  theme,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const themeStyles = {
    primaryColor: theme?.primary_color || '#667eea',
    secondaryColor: theme?.secondary_color || '#764ba2',
    backgroundColor: theme?.background_color || '#ffffff',
    textColor: theme?.text_color || '#333333',
    borderRadius: theme?.border_radius || 8,
  };

  const items = checklist?.items || [];
  const completedCount = items.filter(item => completedItems.includes(item.id)).length;
  const totalCount = items.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isComplete = completedCount === totalCount && totalCount > 0;

  const positionStyle = POSITION_STYLES[checklist?.position] || POSITION_STYLES['bottom-right'];

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleItemClick = useCallback((item) => {
    if (item.tour_id && onStartTour) {
      onStartTour(item.tour_id);
    } else if (item.click_action && onItemClick) {
      onItemClick(item.id, item.click_action);
    }
  }, [onStartTour, onItemClick]);

  const handleItemComplete = useCallback((item) => {
    if (onItemComplete && !completedItems.includes(item.id)) {
      onItemComplete(item.id);
    }
  }, [completedItems, onItemComplete]);

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss(checklist.id);
    }
  }, [checklist?.id, onDismiss]);

  if (!isVisible || !checklist) return null;

  if (isComplete && checklist.auto_dismiss_on_complete) {
    return null;
  }

  return (
    <Fade in={isVisible}>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          ...positionStyle,
          zIndex: 1200,
          width: 320,
          borderRadius: `${themeStyles.borderRadius}px`,
          backgroundColor: themeStyles.backgroundColor,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${themeStyles.primaryColor}, ${themeStyles.secondaryColor})`,
            p: 2,
            cursor: 'pointer',
          }}
          onClick={handleToggleExpand}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: 'white',
                }}
              >
                {checklist.name}
              </Typography>
              {isComplete && (
                <Chip
                  label="Complete"
                  size="small"
                  icon={<CheckCircleIcon sx={{ color: 'white !important', fontSize: 16 }} />}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 24,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDismiss();
                }}
                sx={{ color: 'white', mr: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: 'white' }}>
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          {checklist.show_progress !== false && (
            <Box sx={{ mt: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Progress
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {completedCount} / {totalCount}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'white',
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          )}
        </Box>

        <Collapse in={isExpanded}>
          <List sx={{ py: 1 }}>
            {items.map((item) => {
              const isItemComplete = completedItems.includes(item.id);
              const hasTour = Boolean(item.tour_id);

              return (
                <ListItem
                  key={item.id}
                  disablePadding
                  secondaryAction={
                    hasTour && !isItemComplete ? (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleItemClick(item)}
                        sx={{ color: themeStyles.primaryColor }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                >
                  <ListItemButton
                    onClick={() => {
                      if (!isItemComplete) {
                        if (hasTour) {
                          handleItemClick(item);
                        } else {
                          handleItemComplete(item);
                        }
                      }
                    }}
                    disabled={isItemComplete}
                    sx={{
                      py: 1,
                      '&:hover': {
                        backgroundColor: isItemComplete ? 'transparent' : 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {isItemComplete ? (
                        <CheckCircleIcon
                          sx={{
                            color: themeStyles.primaryColor,
                            fontSize: 22,
                          }}
                        />
                      ) : (
                        <UncheckedIcon
                          sx={{
                            color: 'text.disabled',
                            fontSize: 22,
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isItemComplete ? 'text.disabled' : themeStyles.textColor,
                            textDecoration: isItemComplete ? 'line-through' : 'none',
                          }}
                        >
                          {item.title}
                          {item.is_required && (
                            <Typography
                              component="span"
                              sx={{ color: 'error.main', ml: 0.5 }}
                            >
                              *
                            </Typography>
                          )}
                        </Typography>
                      }
                      secondary={
                        item.description && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              display: 'block',
                              mt: 0.25,
                            }}
                          >
                            {item.description}
                          </Typography>
                        )
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          {checklist.description && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {checklist.description}
              </Typography>
            </Box>
          )}
        </Collapse>
      </Paper>
    </Fade>
  );
};

OnboardingChecklist.propTypes = {
  checklist: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    position: PropTypes.oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left']),
    show_progress: PropTypes.bool,
    auto_dismiss_on_complete: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        is_required: PropTypes.bool,
        tour_id: PropTypes.string,
        click_action: PropTypes.object,
      })
    ).isRequired,
  }),
  completedItems: PropTypes.arrayOf(PropTypes.string),
  isVisible: PropTypes.bool,
  onItemClick: PropTypes.func,
  onItemComplete: PropTypes.func,
  onDismiss: PropTypes.func,
  onStartTour: PropTypes.func,
  theme: PropTypes.shape({
    primary_color: PropTypes.string,
    secondary_color: PropTypes.string,
    background_color: PropTypes.string,
    text_color: PropTypes.string,
    border_radius: PropTypes.number,
  }),
};

export default React.memo(OnboardingChecklist);
